import { type FocusEvent, useEffect, useState } from 'react';
import { Calendar, DateInput, DatePicker, FormField, FormHelp, Label } from '@zillow/constellation-10';
import { format, isValid, parse, startOfDay } from 'date-fns';
import { MOVE_IN_DATE_PAST_ERROR } from '../../utils/validation';

type MoveInDateFieldProps = {
  value: string | Date | null;
  onChange: (value: string | null) => void;
  /** Portal target for the date picker popover. */
  portalRoot?: HTMLElement | null;
  /** Externally-provided error (e.g. a parent form gating submission on a stale past date). */
  error?: string | null;
};

const DATE_FORMAT = 'MM/dd/yyyy';

const validateDateString = (dateStr: string): string | null => {
  if (dateStr === '') return null;
  const parsed = parse(dateStr, DATE_FORMAT, new Date());
  if (!isValid(parsed)) return 'Please enter a valid date (MM/DD/YYYY)';
  if (parsed < startOfDay(new Date())) return MOVE_IN_DATE_PAST_ERROR;
  return null;
};

export const MoveInDateField = ({ value, onChange, portalRoot, error: externalError }: MoveInDateFieldProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    const displayValue = value ? format(value, DATE_FORMAT) : '';
    setDisplayValue(displayValue);
  }, [value]);

  const handleValueChange = (newValue: string | Date) => {
    const formatted = newValue instanceof Date ? format(newValue, DATE_FORMAT) : newValue;
    setError(null);
    setDisplayValue(formatted || '');
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();
    if (inputValue === '') {
      setError(null);
      onChange(null);
      return;
    }
    validateAndChangeForParent(inputValue);
  };

  const handleCalendarSelectedChange = (value: Record<string, string | Date>) => {
    const { formatted } = value;
    validateAndChangeForParent(formatted as string);
  };

  const validateAndChangeForParent = (value: string) => {
    const validationError = validateDateString(value);
    if (validationError) {
      setError(validationError);
      onChange(null);
    } else {
      onChange(value);
    }
  };

  const displayError = error ?? externalError ?? null;

  return (
    <FormField
      key={value ? value.toString() : ''}
      data-testid="move-in-date-field"
      label={<Label data-testid="move-in-date-label">Preferred move-in date</Label>}
      control={
        <DatePicker
          key={value ? value.toString() : 'null'}
          value={displayValue}
          onValueChange={handleValueChange}
          open={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
          minDate={startOfDay(new Date())}
          closeModalOnDateSelect
          calendar={<Calendar onSelectedChange={handleCalendarSelectedChange} />}
          input={<DateInput data-testid="move-in-date-input" onBlur={handleInputBlur} />}
          portalRoot={portalRoot}
        />
      }
      description={
        displayError ? (
          <FormHelp data-testid="move-in-date-error" error>
            {displayError}
          </FormHelp>
        ) : (
          <FormHelp>MM/DD/YYYY</FormHelp>
        )
      }
      error={!!displayError}
    />
  );
};
